import { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Dropdown } from 'antd';
import { Icon as CroplerIcon, Button } from '@cropler/ui-kit';

import styles from './styles.module.scss';

import { DemoWorkspaceId, MapLat, MapLng, Workspace } from '../../constants/keys';
import { setAvatarCompany, setCompanyName } from '../../store/slices/auth';
import { sensorsApi } from '../../store/api/sensors';
import { setCurrentWorkspaceId } from '../../store/slices/workspaces';
import {
  resetSecondarySensor,
  selectDate,
  selectMainSensor,
  selectNextSensor,
  selectPrevSensor,
  updateSorting,
} from '../../store/slices/sensors';
import { statusWorkspaceModal } from '../../store/slices/ui';
import { fieldsApi } from '../../store/api/fields';
import { Avatar } from '../Avatar/index.jsx';
import { BreakpointMobileUp, PathnamesMap } from '../../constants/ui';
import { notesApi } from '../../store/api/notes';
import { membershipsApi } from '../../store/api/memberships';
import { useViewport } from '../../hooks';

const WorkspaceList = memo(({ t }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const workspaceId = localStorage.getItem(Workspace);
  const { widthViewport } = useViewport();

  const [menu, setMenu] = useState([]);
  const [updateMenu, setUpdateMenu] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const { userCompany, avatarCompany, currentUserWorkspaces } = useSelector((state) => state.auth);
  const { currentWorkspaceId } = useSelector((state) => state.workspaces);
  const {
    sorting: { dir, parameter },
  } = useSelector((state) => state.sensors);

  // ----- set workspace -----
  const setWorkspace = useCallback(
    ({ id, name, avatar }) => {
      localStorage.setItem(Workspace, JSON.stringify(id));

      setTimeout(() => {
        dispatch(setCurrentWorkspaceId({ workspaceId: id }));
        dispatch(setCompanyName({ userCompany: name }));
        dispatch(setAvatarCompany({ avatar: avatar }));
        dispatch(updateSorting({ dir, parameter, crop: 'all' }));
        searchParams.delete('tab');
        setSearchParams(searchParams);
        searchParams.delete('date');
        dispatch(selectDate(''));
        setUpdateMenu(true);
        dispatch(resetSecondarySensor());
        dispatch(selectMainSensor(''));
        dispatch(selectNextSensor());
        dispatch(selectPrevSensor());
        localStorage.removeItem(MapLat);
        localStorage.removeItem(MapLng);

        dispatch(sensorsApi.util.resetApiState());
        dispatch(fieldsApi.util.resetApiState());
        dispatch(notesApi.util.resetApiState());
        dispatch(membershipsApi.util.resetApiState());
        if (location.pathname !== `/${PathnamesMap.dashboard}`) {
          // dispatch(ganttApi.util.resetApiState());
          navigate(`/${PathnamesMap.dashboard}`, { replace: true });
        }
      });
    },
    [location.pathname, currentWorkspaceId],
  );

  // -----render workspaces menu-----
  useEffect(() => {
    if (currentUserWorkspaces.length && (!menu.length || updateMenu)) {
      const menuList = [];
      const sortedWorkspaces = [...currentUserWorkspaces].sort((a, b) =>
        a.workspace.name.localeCompare(b.workspace.name, undefined, { sensitivity: 'base' })
      );
      sortedWorkspaces.map((item) => {
        menuList.push({
          label: (
            <div
              className={`${styles.dropdownMenuItem} ${item.workspace.id === workspaceId.replace(/"/g, '') ? styles.dropdownMenuItemActive : ''}`}
            >
              <Button
                className={styles.dropdownMenuBtn}
                type="text"
                onClick={() =>
                  setWorkspace({ id: item.workspace.id, name: item.workspace.name, avatar: item.workspace.avatar })
                }
              >
                {item.workspace.name}
              </Button>
              {item.workspace.id === DemoWorkspaceId && <span className={styles.dropdownMenuLabel}>Demo</span>}
            </div>
          ),
          key: item.id,
        });
      });
      menuList.push({
        label: (
          <div className={`${styles.dropdownMenuItem} ${styles.dropdownMenuItemButton}`}>
            <Button className={styles.dropdownMenuBtn} type="text" onClick={() => dispatch(statusWorkspaceModal(true))}>
              {t('workspaces.buttonAdd')}
            </Button>
          </div>
        ),
        key: 'addWorkspace',
      });
      setMenu(menuList);
      setUpdateMenu(false);
    }
  }, [updateMenu, currentUserWorkspaces]);

  useEffect(() => {
    if (!updateMenu && menu.length) {
      setUpdateMenu(true);
    }
  }, [currentUserWorkspaces]);

  return (
    <>
      {!!menu.length && (
        <Dropdown
          menu={{
            items: menu,
          }}
          trigger={['click']}
        >
          <span className={styles.dropdownMenuToggle}>
            <Button
              type={widthViewport < BreakpointMobileUp ? 'text' : 'secondary'}
              className={styles.dropdownMenuToggleButton}
            >
              <Avatar size={'small'} imgUrl={avatarCompany} alt={userCompany} />
              <span className={styles.dropdownMenuToggleText}>{userCompany}</span>
              <CroplerIcon name="down" />
            </Button>
          </span>
        </Dropdown>
      )}
    </>
  );
});

export default WorkspaceList;

WorkspaceList.displayName = 'WorkspaceList';
